import * as React from 'react';
import { Label } from './label';
import { cn } from '../lib/utils';

interface FieldProps {
  label: React.ReactNode;
  /** Rendered under the control; switches to the error style when `error` is set. */
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** Receives the generated id so the label and message wire up correctly. */
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => React.ReactNode;
}

/**
 * Label + control + message, with the aria wiring done once rather than
 * per-page. Replaces MUI's FormControl/InputLabel/helperText trio.
 */
const Field = ({ label, hint, error, required, className, children }: FieldProps) => {
  const id = React.useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-danger">
            *
          </span>
        )}
      </Label>
      {children({
        id,
        'aria-describedby': message ? messageId : undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {message && (
        <p
          id={messageId}
          className={cn('font-sans text-xs', error ? 'text-danger' : 'text-ink-soft')}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export { Field };
